export const getVideoId = (item) => {
  if (!item || typeof item !== 'object') {
    return undefined;
  }
  const raw = item.id ?? item.Id ?? item.videoId ?? item.VideoId;
  if (raw == null || raw === '') {
    return undefined;
  }
  return String(raw);
};

const resolveCategoryIds = (item) => {
  const ids = item.categoryIds ?? item.CategoryIds;
  if (Array.isArray(ids) && ids.length) {
    return ids.map((id) => String(id));
  }
  const cats = item.categories ?? item.Categories;
  if (Array.isArray(cats) && cats.length) {
    return cats
      .map((c) => c?.id ?? c?.Id)
      .filter((id) => id != null && id !== '')
      .map(String);
  }
  const single =
    item.category ?? item.Category ?? item.categoryId ?? item.CategoryId;
  return single != null && single !== '' ? [String(single)] : [];
};

const resolveCategoryLabel = (item) => {
  const cats = item.categories ?? item.Categories;
  if (Array.isArray(cats) && cats.length) {
    return cats
      .map((c) => c?.title ?? c?.Title ?? '')
      .filter(Boolean)
      .join(', ');
  }
  return item.categoryName ?? item.CategoryName ?? item.categoryTitle ?? '';
};

/** Map GET /videos and GET /video/{videoId} rows to UI-friendly fields. */
export const normalizeVideoRow = (item) => {
  const id = getVideoId(item);
  const categoryIds = resolveCategoryIds(item);
  const category = categoryIds[0] ?? '';

  return {
    ...item,
    id,
    title: item.title ?? item.Title ?? '',
    shortDescription: item.shortDescription ?? item.ShortDescription ?? '',
    longDescription: item.longDescription ?? item.LongDescription ?? '',
    fileSizeBytes: item.fileSizeBytes ?? item.FileSizeBytes ?? null,
    originalFileName:
      item.originalFileName ?? item.OriginalFileName ?? '',
    videoUrl:
      item.videoURL ??
      item.videoUrl ??
      item.VideoURL ??
      item.VideoUrl ??
      '',
    hlsUrl:
      item.hlsurl ??
      item.hlsUrl ??
      item.HlsUrl ??
      item.HLSURL ??
      item.hlsURL ??
      '',
    thumbnail:
      item.thumbnailURL ??
      item.ThumbnailURL ??
      item.thumbnailUrl ??
      item.ThumbnailUrl ??
      item.thumbnail ??
      item.Thumbnail ??
      '',
    category,
    categoryIds,
    categories: item.categories ?? item.Categories ?? [],
    isPublished: Boolean(item.isPublished ?? item.IsPublished ?? false),
    isFeatured: Boolean(item.isFeatured ?? item.IsFeatured ?? false),
    categoryName: resolveCategoryLabel(item),
    createdAt: item.createdAt ?? item.CreatedAt ?? '',
    updatedAt: item.updatedAt ?? item.UpdatedAt ?? '',
  };
};

/**
 * Reads the selected antd Upload file from a form field value.
 */
export function getUploadOriginFile(fileList) {
  if (!fileList) return null;

  const list = Array.isArray(fileList) ? fileList : fileList.fileList;
  if (!Array.isArray(list) || !list.length) return null;

  const entry = list[list.length - 1] ?? list[0];
  if (entry?.originFileObj instanceof File) {
    return entry.originFileObj;
  }
  if (entry instanceof File) {
    return entry;
  }
  return null;
}

/**
 * Multipart body for POST /video and PUT /video/{videoId}.
 * Field names match Video Management API (PascalCase per OpenAPI).
 */
export const buildVideoFormData = ({
  title,
  shortDescription,
  longDescription,
  videoFile,
  thumbnailFile,
  categoryIds,
  isPublished,
  isFeatured,
  hlsUrl,
}) => {
  const formData = new FormData();
  formData.append('Title', (title ?? '').trim());
  formData.append('ShortDescription', (shortDescription ?? '').trim());
  formData.append('LongDescription', (longDescription ?? '').trim());

  const ids = Array.isArray(categoryIds)
    ? categoryIds
    : categoryIds
      ? [categoryIds]
      : [];
  ids.filter(Boolean).forEach((catId) => {
    formData.append('CategoryIds', String(catId));
  });

  if (typeof isPublished === 'boolean') {
    formData.append('IsPublished', String(isPublished));
  }
  if (typeof isFeatured === 'boolean') {
    formData.append('IsFeatured', String(isFeatured));
  }
  if (hlsUrl) {
    formData.append('HLSURL', hlsUrl);
  }
  if (videoFile instanceof File) {
    formData.append('VideoFile', videoFile, videoFile.name || 'video.mp4');
  }
  if (thumbnailFile instanceof File) {
    formData.append(
      'ThumbnailFile',
      thumbnailFile,
      thumbnailFile.name || 'thumbnail.jpg',
    );
  }
  return formData;
};

export const parseVideoListPayload = (payload) => {
  if (Array.isArray(payload)) {
    return {list: payload, total: payload.length};
  }
  if (payload && Array.isArray(payload.data)) {
    return {
      list: payload.data,
      total:
        typeof payload.total === 'number'
          ? payload.total
          : payload.totalCount ?? payload.count ?? payload.data.length,
    };
  }
  if (payload && Array.isArray(payload.items)) {
    return {
      list: payload.items,
      total: payload.total ?? payload.items.length,
    };
  }
  if (payload && Array.isArray(payload.videos)) {
    return {
      list: payload.videos,
      total: payload.total ?? payload.videos.length,
    };
  }
  return {list: [], total: 0};
};
